import React, {
  useCallback, useMemo, useRef, useState,
} from 'react';

import { createRoot } from 'react-dom/client';
import PropTypes from 'prop-types';
import { Button, InfoBlock } from '@picsio/ui';

import localization from '../../../../shared/strings';
import Checkbox from '../../../../UIComponents/checkbox';
import RadioItem from '../../../RadioItem';
import Tooltip from '../../../Tooltip';
import Icon from '../../../Icon';
import {
  buildInitialChannelActions,
  CHANNEL_SYNC_ACTION,
  getDefaultChannelAction,
  getDefaultPublishingScope,
  hasAttachedMediaForSync,
  isMediaScopeAvailable,
} from '../helpers/syncFlowOrchestrator';
import { getListingStatusText, hasDraftListingForChannel, hasLiveListingForChannel } from '../helpers/syncFlowHelpers';
import ButtonLoader from '../ButtonLoader';
import styles from '../styles.module.scss';

const buildInitialCheckedChannels = (channelOptions, initialSetupState) => {
  if (initialSetupState?.selectedChannels?.length) {
    return channelOptions.reduce((acc, option) => ({
      ...acc,
      [option.value]: !option.disconnected && initialSetupState.selectedChannels.includes(option.value),
    }), {});
  }

  const selectableOptions = channelOptions.filter((option) => !option.disconnected);

  return channelOptions.reduce((acc, option) => ({
    ...acc,
    [option.value]: !option.disconnected && selectableOptions.length === 1,
  }), {});
};

function PublishingScopeSetupDialog({
  channelOptions,
  productListings,
  product,
  productsState,
  isVariantSync,
  masterVariantIds,
  shouldShowVariantNote,
  initialSetupState,
  onProceed,
  onCancel,
  onValidationAbort,
  root,
}) {
  const strings = localization.DIALOGS.PRODUCT_DIALOGS.CONNECT_CHANNEL;
  const setupStrings = strings.SETUP_PUBLISHING_SCOPE;
  const scopeStrings = strings.PUBLISHING_SCOPE;

  const initialChannelActions = useMemo(
    () => buildInitialChannelActions(channelOptions, productListings),
    [channelOptions, productListings],
  );

  const defaultScope = useMemo(
    () => getDefaultPublishingScope({
      product,
      isVariantSync,
      masterVariantIds,
      productsState,
    }),
    [isVariantSync, masterVariantIds, product, productsState],
  );

  const isMediaAvailable = useMemo(
    () => isMediaScopeAvailable({
      product,
      isVariantSync,
      masterVariantIds,
      productsState,
    }),
    [isVariantSync, masterVariantIds, product, productsState],
  );

  const mediaDisabledTooltip = useMemo(() => {
    if (isMediaAvailable) {
      return null;
    }

    if (!hasAttachedMediaForSync({
      product, isVariantSync, masterVariantIds, productsState,
    })) {
      return scopeStrings.MEDIA_NO_ASSETS_TOOLTIP;
    }

    return null;
  }, [
    isMediaAvailable,
    isVariantSync,
    masterVariantIds,
    product,
    productsState,
    scopeStrings.MEDIA_NO_ASSETS_TOOLTIP,
  ]);

  const [checkedChannels, setCheckedChannels] = useState(
    () => buildInitialCheckedChannels(channelOptions, initialSetupState),
  );
  const [channelActions, setChannelActions] = useState(() => (
    initialSetupState?.channelActionsByChannelId
      ? { ...initialChannelActions, ...initialSetupState.channelActionsByChannelId }
      : initialChannelActions
  ));
  const [publishingScope, setPublishingScope] = useState(
    () => initialSetupState?.publishingScope ?? defaultScope,
  );
  const [isProceeding, setIsProceeding] = useState(false);

  const selectedChannelIds = useMemo(
    () => Object.entries(checkedChannels)
      .filter(([, checked]) => checked)
      .map(([channelId]) => channelId),
    [checkedChannels],
  );

  const hasChannelSelection = selectedChannelIds.length > 0;
  const hasScopeSelection = publishingScope.text || publishingScope.media;
  const showVariantNote = isVariantSync && selectedChannelIds.some(
    (channelId) => shouldShowVariantNote?.(channelId),
  );

  const proceedLabel = publishingScope.media
    ? strings.BTN_PROCEED_TO_MEDIA
    : strings.BTN_PROCEED;

  const shouldValidateOnProceed = publishingScope.text && !publishingScope.media;

  const proceedTooltip = useMemo(() => {
    if (!hasChannelSelection && !hasScopeSelection) {
      return strings.SELECT_CHANNEL_AND_SCOPE_TOOLTIP;
    }

    if (!hasChannelSelection) {
      return strings.SELECT_CHANNEL_TOOLTIP;
    }

    if (!hasScopeSelection) {
      return strings.SELECT_SCOPE_TOOLTIP;
    }

    return null;
  }, [
    hasChannelSelection,
    hasScopeSelection,
    strings.SELECT_CHANNEL_AND_SCOPE_TOOLTIP,
    strings.SELECT_CHANNEL_TOOLTIP,
    strings.SELECT_SCOPE_TOOLTIP,
  ]);

  const isDestroyedRef = useRef(false);

  const destroy = useCallback(() => {
    if (isDestroyedRef.current) {
      return;
    }

    isDestroyedRef.current = true;

    try {
      root.unmount();
    } catch { /* ignore */ }
  }, [root]);

  const handleCancel = useCallback(() => {
    destroy();
    onCancel?.();
  }, [destroy, onCancel]);

  const handleProceed = useCallback(async () => {
    const selectedChannelActions = selectedChannelIds.reduce((acc, channelId) => ({
      ...acc,
      [channelId]: channelActions[channelId] || getDefaultChannelAction(channelId, productListings),
    }), {});

    const payload = {
      selectedChannels: selectedChannelIds,
      channelActionsByChannelId: selectedChannelActions,
      publishingScope,
      overrideExisting: true,
    };

    if (shouldValidateOnProceed) {
      setIsProceeding(true);

      try {
        await onProceed(payload, {
          onUnmappedDialogOpen: () => {
            setIsProceeding(false);
            destroy();
          },
        });
        destroy();
      } catch {
        if (isDestroyedRef.current) {
          onValidationAbort?.();
          return;
        }

        setIsProceeding(false);
      }

      return;
    }

    destroy();
    onProceed(payload);
  }, [
    channelActions,
    destroy,
    onProceed,
    productListings,
    publishingScope,
    selectedChannelIds,
    shouldValidateOnProceed,
    onValidationAbort,
  ]);

  const handleChannelToggle = useCallback((channelId) => {
    const option = channelOptions.find(({ value }) => value === channelId);
    if (option?.disconnected) {
      return;
    }

    setCheckedChannels((prev) => ({ ...prev, [channelId]: !prev[channelId] }));
  }, [channelOptions]);

  const handleChannelActionChange = useCallback((channelId, action) => {
    setChannelActions((prev) => ({ ...prev, [channelId]: action }));
  }, []);

  const handleTextToggle = useCallback(() => {
    setPublishingScope((prev) => ({ ...prev, text: !prev.text }));
  }, []);

  const handleMediaToggle = useCallback(() => {
    if (!isMediaAvailable) return;
    setPublishingScope((prev) => ({ ...prev, media: !prev.media }));
  }, [isMediaAvailable]);

  const getChannelReplaceWarningText = useCallback((channelId, isSelected, channelAction) => {
    if (!isSelected) {
      return null;
    }

    if (channelAction === CHANNEL_SYNC_ACTION.DRAFT && hasDraftListingForChannel({
      channelId,
      product,
      productsState,
      productListings,
      masterVariantIds,
    })) {
      return setupStrings.EXISTING_DRAFT_WILL_BE_REPLACED;
    }

    if (channelAction === CHANNEL_SYNC_ACTION.PUBLISH && hasLiveListingForChannel({
      channelId,
      product,
      productsState,
      productListings,
      masterVariantIds,
    })) {
      return setupStrings.EXISTING_LIVE_WILL_BE_REPLACED;
    }

    return null;
  }, [masterVariantIds, product, productListings, productsState, setupStrings]);

  const getChannelStatusText = useCallback((channelId, isDisconnected) => {
    if (isDisconnected) {
      return setupStrings.CONNECTION_LOST;
    }

    const statusText = getListingStatusText(productListings?.[channelId]);

    if (statusText === 'Not set up yet') {
      return setupStrings.NOT_PUBLISHED_YET;
    }

    return statusText;
  }, [productListings, setupStrings.CONNECTION_LOST, setupStrings.NOT_PUBLISHED_YET]);

  return (
    <div className={`simpleDialog ${styles.connectChannelSectionModal} ${styles.connectChannelSectionDialogWithSubheader}`}>
      <div className="simpleDialogUnderlayer" />
      <div className={`simpleDialogBox ${styles.connectChannelSectionModalBox}`}>
        <div className="simpleDialogHeader">
          <span className="simpleDialogTitle">{setupStrings.TITLE}</span>
          <span
            role="button"
            tabIndex={0}
            className="simpleDialogBtnCross"
            aria-disabled={isProceeding}
            onClick={handleCancel}
            onKeyDown={handleCancel}
          >
            <Icon name="close" />
          </span>
        </div>
        <div className="simpleDialogSubHeader">
          <p>{setupStrings.DESCRIPTION}</p>
        </div>
        <div className="simpleDialogContent">
          <div className="simpleDialogContentInner">
            <If condition={showVariantNote}>
              <InfoBlock className="m-b-12">{strings.VARIANT_NOTE}</InfoBlock>
            </If>

            <div className={styles.connectChannelSectionSetupSection}>
              <div className={styles.connectChannelSectionSetupSectionTitle}>
                {setupStrings.TARGET_CHANNELS_TITLE}
              </div>
              <div className={styles.connectChannelSectionSetupSectionDivider} />
              {channelOptions.map((option, index) => {
                const isDisconnected = Boolean(option.disconnected);
                const isSelected = checkedChannels[option.value];
                const channelAction = channelActions[option.value];
                const replaceWarningText = getChannelReplaceWarningText(
                  option.value,
                  isSelected,
                  channelAction,
                );

                const channelRow = (
                  <div className={styles.connectChannelSectionChannelRow}>
                    <div className={styles.connectChannelSectionChannelRowMain}>
                      <Checkbox
                        className="w-100"
                        value={checkedChannels[option.value]}
                        disabled={isDisconnected || isProceeding}
                        label={(
                          <span className={styles.connectChannelSectionChannelRowLabel}>
                            <span className={styles.connectChannelSectionChannelRowTitle}>
                              <If condition={option.icon}>
                                <span className={styles.connectChannelSectionChannelRowIcon}>
                                  {option.icon}
                                </span>
                              </If>
                              <span className={styles.connectChannelSectionChannelRowName}>
                                {option.label}
                              </span>
                            </span>
                            <If condition={replaceWarningText}>
                              <span className={styles.connectChannelSectionChannelRowDraftWarning}>
                                {replaceWarningText}
                              </span>
                            </If>
                            <span className={styles.connectChannelSectionChannelRowStatus}>
                              ({getChannelStatusText(option.value, isDisconnected)})
                            </span>
                          </span>
                        )}
                        onChange={() => handleChannelToggle(option.value)}
                      />
                    </div>
                    <If condition={isSelected}>
                      <div className={styles.connectChannelSectionChannelRowActions}>
                        <div className={styles.connectChannelSectionChannelActionGroup}>
                          <RadioItem
                            value={CHANNEL_SYNC_ACTION.DRAFT}
                            checkedValue={channelAction}
                            label={setupStrings.DRAFT_ACTION_LABEL}
                            onChange={(value) => handleChannelActionChange(option.value, value)}
                          />
                        </div>
                        <div className={styles.connectChannelSectionChannelActionGroup}>
                          <RadioItem
                            value={CHANNEL_SYNC_ACTION.PUBLISH}
                            checkedValue={channelAction}
                            label={setupStrings.PUBLISHED_ACTION_LABEL}
                            onChange={(value) => handleChannelActionChange(option.value, value)}
                          />
                        </div>
                      </div>
                    </If>
                  </div>
                );

                const rowElement = isDisconnected ? (
                  <Tooltip content={setupStrings.CONNECTION_LOST_TOOLTIP}>
                    {channelRow}
                  </Tooltip>
                ) : channelRow;

                return (
                  <React.Fragment key={option.value}>
                    {rowElement}
                    <If condition={index < channelOptions.length - 1}>
                      <div
                        className={styles.connectChannelSectionChannelRowDivider}
                        data-testid="channel-row-divider"
                      />
                    </If>
                  </React.Fragment>
                );
              })}
            </div>

            <div className={styles.connectChannelSectionSetupSection}>
              <div className={styles.connectChannelSectionSetupSectionTitle}>
                {setupStrings.DATA_TO_SYNCHRONIZE_TITLE}
              </div>
              <div className={styles.connectChannelSectionSetupSectionDivider} />
              <div className={styles.connectChannelSectionScopeOption}>
                <Checkbox
                  value={publishingScope.text}
                  label={(
                    <span className={styles.connectChannelSectionScopeLabel}>
                      <span className={styles.connectChannelSectionScopeTitle}>
                        {scopeStrings.TEXT_ATTRIBUTES_TITLE}
                      </span>
                      <span className={styles.connectChannelSectionScopeDescription}>
                        {scopeStrings.TEXT_ATTRIBUTES_DESCRIPTION}
                      </span>
                    </span>
                  )}
                  onChange={handleTextToggle}
                />
              </div>
              <Tooltip content={mediaDisabledTooltip}>
                <div className={styles.connectChannelSectionScopeOption}>
                  <Checkbox
                    value={publishingScope.media}
                    disabled={!isMediaAvailable}
                    label={(
                      <span className={styles.connectChannelSectionScopeLabel}>
                        <span className={styles.connectChannelSectionScopeTitle}>
                          {scopeStrings.MEDIA_ATTRIBUTES_TITLE}
                        </span>
                        <span className={styles.connectChannelSectionScopeDescription}>
                          {scopeStrings.MEDIA_ATTRIBUTES_DESCRIPTION}
                        </span>
                      </span>
                    )}
                    onChange={handleMediaToggle}
                  />
                </div>
              </Tooltip>
            </div>
          </div>
        </div>
        <div className="simpleDialogFooter">
          <Button
            color="secondary"
            size="md"
            variant="contained"
            disabled={isProceeding}
            onClick={handleCancel}
          >
            {strings.BTN_CANCEL}
          </Button>
          <Tooltip content={proceedTooltip}>
            <Button
              color="primary"
              size="md"
              variant="contained"
              disabled={!hasChannelSelection || !hasScopeSelection || isProceeding}
              endIcon={isProceeding ? <ButtonLoader /> : null}
              onClick={handleProceed}
            >
              {proceedLabel}
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}

PublishingScopeSetupDialog.propTypes = {
  channelOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    type: PropTypes.string,
    icon: PropTypes.node,
    disconnected: PropTypes.bool,
  })).isRequired,
  productListings: PropTypes.objectOf(PropTypes.shape({})),
  product: PropTypes.shape({}),
  productsState: PropTypes.shape({}),
  isVariantSync: PropTypes.bool,
  masterVariantIds: PropTypes.arrayOf(PropTypes.string),
  shouldShowVariantNote: PropTypes.func,
  initialSetupState: PropTypes.shape({
    selectedChannels: PropTypes.arrayOf(PropTypes.string),
    channelActionsByChannelId: PropTypes.objectOf(PropTypes.string),
    publishingScope: PropTypes.shape({
      text: PropTypes.bool,
      media: PropTypes.bool,
    }),
  }),
  root: PropTypes.shape({ unmount: PropTypes.func.isRequired }).isRequired,
  onProceed: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  onValidationAbort: PropTypes.func,
};

PublishingScopeSetupDialog.defaultProps = {
  productListings: undefined,
  product: undefined,
  productsState: undefined,
  isVariantSync: false,
  masterVariantIds: undefined,
  shouldShowVariantNote: () => false,
  initialSetupState: undefined,
  onCancel: undefined,
  onValidationAbort: undefined,
};

export function showPublishingScopeSetupDialog({
  channelOptions,
  productListings,
  product,
  productsState,
  isVariantSync,
  masterVariantIds,
  shouldShowVariantNote,
  initialSetupState,
  onProceed,
  onCancel,
  onValidationAbort,
}) {
  const $wrapper = document.querySelector('.wrapperDialog');
  const root = createRoot($wrapper);
  root.render(
    <PublishingScopeSetupDialog
      channelOptions={channelOptions}
      productListings={productListings}
      product={product}
      productsState={productsState}
      isVariantSync={isVariantSync}
      masterVariantIds={masterVariantIds}
      shouldShowVariantNote={shouldShowVariantNote}
      initialSetupState={initialSetupState}
      root={root}
      onProceed={onProceed}
      onCancel={onCancel}
      onValidationAbort={onValidationAbort}
    />,
  );
}

export default PublishingScopeSetupDialog;
